import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useStoreSelectionStore, selectCurrencySymbol } from "@/stores/store-selection-store";
import { useShallow } from "zustand/react/shallow";
import { useCartData } from "@/hooks/useCartData";
import { BasketIcon } from "@/components/icons/SvgIcons";
import { DominosLogoBlue } from "@/components/icons/SvgIcons";
import { testAttrs } from "@/utils/testAttrs";

export default function HomepageHeader() {
  const isMobile = useIsMobile();
  
  const { storeId, postcode } = useStoreSelectionStore(
    useShallow((s) => ({
      storeId: s.storeId || "",
      postcode: s.postcode || "",
    })),
  );

  const cartData = useCartData();
  const cartCount = cartData?.cartCount || 0;
  const formattedTotal = cartData?.formattedTotal || "0.00";
  const currencySymbol = useStoreSelectionStore(selectCurrencySymbol) || "£";

  const orderType = "delivery"; // Default for homepage

  return (
    <header className="fixed top-0 left-0 z-50 w-full bg-white shadow-lg border-b border-gray-200">
      {/* Mobile Homepage Header */}
      {isMobile ? (
        <div className="flex items-center justify-between px-4 py-3 h-16 relative">
          {/* Basket Icon - Left side when no store, Right side when store exists */}
          {storeId ? (
            <Link
              to={`/basket?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}`}
              className="flex h-8 p-2 hover:bg-gray-100 items-center border-l border-gray-300 pl-4 absolute right-0"
              aria-label="View basket"
            >
              <div className="relative">
                <BasketIcon
                  width={20}
                  height={20}
                  className="text-dominos-green shrink-0"
                />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-dominos-red-bright text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </div>
              <span className="text-dominos-green text-[12px] dominos-secondary pl-2 py-0.5">
                {currencySymbol}
                {formattedTotal}
              </span>
            </Link>
          ) : (
            <div className="absolute left-0 w-10"></div>
          )}
          
          {/* Dominos Logo - Perfectly Centered */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <Link
              to={storeId ? "/store-information" : "/"}
              className="flex items-center justify-center"
              {...testAttrs({ "data-testid": "dominos-logo-link" })}
            >
              <DominosLogoBlue className="w-[140px] h-12" />
            </Link>
          </div>
          
          {/* Spacer on left when basket is on right */}
          {storeId && <div className="absolute left-0 w-10"></div>}
        </div>
      ) : (
        /* Desktop Homepage Header */
        <div className="page-container h-full py-4">
          <div className="flex items-center justify-between h-16">
            {/* Left spacer */}
            <div className="flex-1"></div>
            
            {/* Dominos Logo - Centered */}
            <div className="flex-1 flex items-center justify-center">
              <Link
                to={storeId ? "/store-information" : "/"}
                className="inline-flex items-center justify-center"
                {...testAttrs({ "data-testid": "dominos-logo-link" })}
              >
                <DominosLogoBlue className="w-[216px] h-14" />
              </Link>
            </div>
            
            {/* Right side - Basket */}
            <div className="flex-1 flex items-center justify-end">
              {storeId && (
                <Link
                  to={`/basket?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}`}
                  className="flex items-center hover:bg-gray-100 p-2 rounded transition-colors"
                  aria-label="View basket"
                >
                  <div className="relative">
                    <BasketIcon
                      width={24}
                      height={24}
                      className="text-dominos-green shrink-0"
                    />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-dominos-red-bright text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </div>
                  <span className="text-dominos-green text-[14px] dominos-secondary pl-3 py-0.5">
                    {currencySymbol}
                    {formattedTotal}
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
